import React from 'react';

export type CarrierTabId =
  | 'overview'
  | 'inspections'
  | 'crashes'
  | 'violations'
  | 'removed-violations'
  | 'vins'
  | 'basics'
  | 'predictions'
  | 'analytics'
  | 'network'
  | 'status-history'
  | 'fair-assessment';

export interface TabNavigationProps {
  activeTab: CarrierTabId;
  setActiveTab: (tab: CarrierTabId) => void;
  basicsCount: number;
  violationsCount: number;
  inspectionsCount: number;
  crashesCount: number;
  vehiclesCount: number;
  // Extra tab buttons (e.g. Authority & Insurance) rendered in the same
  // flex-wrap row as the buttons below, so they wrap and align with them
  // instead of sitting in a separate box underneath.
  children?: React.ReactNode;
}

const TabNavigation: React.FC<TabNavigationProps> = ({
  activeTab,
  setActiveTab,
  basicsCount,
  violationsCount,
  inspectionsCount,
  crashesCount,
  vehiclesCount,
  children,
}) => {
  const tabs: Array<{ id: CarrierTabId; label: string; count: number | null }> = [
    { id: 'overview', label: 'Overview', count: null },
    { id: 'analytics', label: 'Analytics', count: null },
    { id: 'basics', label: 'BASIC Scores', count: basicsCount },
    { id: 'predictions', label: 'Predictions', count: null },
    { id: 'fair-assessment', label: 'Fair Assessment', count: null },
    { id: 'violations', label: 'Violations', count: violationsCount },
    { id: 'removed-violations', label: 'Removed Violations', count: null },
    { id: 'inspections', label: 'Inspections', count: inspectionsCount },
    { id: 'crashes', label: 'Crashes', count: crashesCount },
    { id: 'vins', label: 'VINs', count: vehiclesCount },
    { id: 'status-history', label: 'Status History', count: null },
    { id: 'network', label: 'Network', count: null },
  ];

  // Washi tabs: plain text with an ink underline for the active tab; scrolls horizontally instead of wrapping
  return (
    <div
      role="tablist"
      className="flex items-end gap-7 overflow-x-auto pr-12 border-b border-gray-200 mb-6 [mask-image:linear-gradient(to_right,black_calc(100%-48px),transparent)]"
    >
      {tabs.map((tab) => {
        const isActive = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            role="tab"
            aria-selected={isActive}
            onClick={() => setActiveTab(tab.id)}
            className={`-mb-px shrink-0 whitespace-nowrap py-3 text-sm border-b transition-colors ${
              isActive ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-900'
            }`}
          >
            {tab.label}
            {tab.count !== null && (
              <span className="ml-1.5 text-xs text-gray-400 tabular-nums">{tab.count}</span>
            )}
          </button>
        );
      })}
      {children}
    </div>
  );
};

export default TabNavigation;
